// utils/haulerPayDisclosure.js
// Centralized Hauler v2 disclosure policy.
//
// Before verification, Hauler surfaces show a structurally-fixed earnings
// band based on booked tier +/- one adjacent tier. Exact tier/pay details are
// reserved for completed jobs or the verification submit step.
//
// Source-of-truth path for derived display values:
//   MARKETS[].tier_prices -> getTierPrice() -> calcSplit()
// This module does not create chargeable prices or mutate booking economics.

import { calcSplit, getTierPrice } from "./marketConfig";
import { DEFAULT_MARKET_ID } from "./common";

export const HAULER_DISCLOSURE_FLAG = "REACT_APP_HAULER_PAY_BAND_ENABLED";
export const HAULER_TIER_ORDER = ["Small", "Half", "ThreeQ", "Full"];

export function isHaulerPayBandEnabled() {
  return process.env.REACT_APP_HAULER_PAY_BAND_ENABLED !== "false";
}

export function isPostVerificationJob(job) {
  if (!job) return false;
  if (job.status === "complete" || job.completed_at) return true;
  const persisted = Number(job.driver_pay_amt);
  return Number.isFinite(persisted) && persisted > 0;
}

export function shouldShowExactHaulerPay(job) {
  return isPostVerificationJob(job);
}

export function deriveHaulerPayBand(job) {
  if (!job?.size) return null;

  const idx = HAULER_TIER_ORDER.indexOf(job.size);
  if (idx < 0) return null;

  const lowerKey = HAULER_TIER_ORDER[Math.max(0, idx - 1)];
  const upperKey = HAULER_TIER_ORDER[Math.min(HAULER_TIER_ORDER.length - 1, idx + 1)];
  const marketId = job.market_id || DEFAULT_MARKET_ID;
  const zoneKey = job.zone || null;

  try {
    const lowerGross = getTierPrice(marketId, lowerKey);
    const upperGross = getTierPrice(marketId, upperKey);
    if (lowerGross == null || upperGross == null) return null;

    const isQuick = job.quick === true;
    const lower = calcSplit(lowerGross, marketId, zoneKey, { quick: isQuick });
    const upper = calcSplit(upperGross, marketId, zoneKey, { quick: isQuick });
    const lowPay = Math.min(lower.driverPay, upper.driverPay);
    const highPay = Math.max(lower.driverPay, upper.driverPay);

    return {
      lowerTierKey: lowerKey,
      upperTierKey: upperKey,
      lowerGross,
      upperGross,
      lowPay,
      highPay,
      midpointPay: Math.round(((lowPay + highPay) / 2) * 100) / 100,
      isBoundaryBand: lowerKey === upperKey,
    };
  } catch {
    return null;
  }
}

export function formatCurrency(value) {
  if (value == null || isNaN(value)) return "$0.00";
  return "$" + Number(value).toFixed(2);
}

export function formatPayBand(band) {
  if (!band) return "Range pending";
  if (Math.abs(band.lowPay - band.highPay) < 0.01) {
    return formatCurrency(band.lowPay);
  }
  return `${formatCurrency(band.lowPay)} - ${formatCurrency(band.highPay)}`;
}

export function getHaulerPayDisclosure(job, economics = null) {
  if (shouldShowExactHaulerPay(job)) {
    const persisted = Number(job?.driver_pay_amt);
    const exactPay = Number.isFinite(persisted) && persisted > 0
      ? persisted
      : Number(economics?.driverPay || 0);

    return {
      mode: "exact",
      label: "You earned",
      payText: formatCurrency(exactPay),
      detailText: "Final payout recorded for this job",
      exactPay,
      payBand: null,
      showExactBreakdown: true,
      showTierLabel: true,
      sortValue: exactPay,
    };
  }

  const band = deriveHaulerPayBand(job);

  return {
    mode: "band",
    label: "Estimated earnings range",
    payText: formatPayBand(band),
    detailText: "Exact payout appears after verification",
    exactPay: null,
    payBand: band,
    showExactBreakdown: false,
    showTierLabel: false,
    sortValue: band?.midpointPay || 0,
  };
}

export function haulerPaySortValue(job) {
  const disclosure = getHaulerPayDisclosure(job);
  return disclosure.sortValue || 0;
}

export function haulerScopeLabel(job) {
  if (job?.type) return String(job.type);
  return "Pickup scope";
}
